import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../../../constants/aampeTheme';

// context
import { EditorContext } from '../../../../../context/EditorContext';

// helpers
import debounce from '../../../../../helpers/debounce';

import useGetCurrentMessageContent from '../../../../../hooks/message-content/useGetCurrentMessageContent';
import useMessageContentGetAndUpdate from '../../../../../hooks/message-content/useMessageContentGetAndUpdate';
// hooks
import useVariantDelete from 'hooks/variants/useVariantDelete';
import useVariantUpdate from 'hooks/variants/useVariantUpdate';

import { VariantWithLabel } from 'models/variant.model';

// components
import AlternateListItemContent from './AlternateListItemContent';
import { getUpdatedFormulaStructure } from './helper';

interface StyledProps {
    index: number;
    highlighted: boolean;
    selected?: boolean;
    isMasterVariant?: boolean;
    isPasteButtonShow?: boolean;
}

interface AlternateListItemProps {
    alternateData: VariantWithLabel;
    index: number;
    isEditable: boolean;
    listView?: boolean;
    selectedAlternate: string | null;
    setSelectedAlternate: (value: string | null) => void;
    isPasteButtonShow?: boolean;
}

const AlternateListItem: React.FC<
    React.PropsWithChildren<AlternateListItemProps>
> = ({
    alternateData,
    index,
    isEditable,
    selectedAlternate,
    setSelectedAlternate,
    isPasteButtonShow,
}) => {
    const { editorDetails, setEditorDetails } = useContext(EditorContext);
    const inputRef = useRef<any>();
    const cursorPositionRef = useRef<number>();
    // Track when emoji selection is in progress to prevent blur handler from overwriting
    const isEmojiSelectingRef = useRef<boolean>(false);
    const initialText = alternateData.content;
    const [text, setText] = useState(initialText);
    const { formula } = editorDetails;
    const [itemError, setItemError] = useState<string | undefined>(undefined);

    const selected = useMemo<boolean>(() => {
        return selectedAlternate === alternateData.id;
    }, [alternateData.id, selectedAlternate]);

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [highlighted, setHighlighted] = useState(false);

    const { currentMessageContentId } = useGetCurrentMessageContent(
        formula?.id,
    );

    useEffect(() => {
        if (cursorPositionRef.current !== undefined) {
            inputRef.current.setSelectionRange(
                cursorPositionRef.current,
                cursorPositionRef.current,
            );
            cursorPositionRef.current = undefined;
        }
    }, [text]);

    useEffect(() => {
        if (editorDetails.newly_created_alternate_id === alternateData.id) {
            setHighlighted(true);
            setTimeout(() => {
                setHighlighted(false);
            }, 1000);
            setEditorDetails({
                ...editorDetails,
                newly_created_alternate_id: '',
            });
        }
    }, [alternateData, editorDetails, setEditorDetails]);

    const updateMutation = useVariantUpdate(
        alternateData.component_id,
        formula?.id as number,
    );

    const messageContentUpdate = useMessageContentGetAndUpdate();

    const debounceContentUpdate = debounce(messageContentUpdate.mutate, 1500);

    useEffect(() => {
        if (alternateData.id === alternateData.component_id) {
            setText(initialText);
        }
    }, [initialText, alternateData]);
    const deleteAlternateMutation = useVariantDelete(
        alternateData.component_id,
        formula?.id as number,
    );

    const isMasterVariant = alternateData.id === alternateData.component_id;
    const saveChanges = (e: any) => {
        const value = e.target.value;
        if (value === '' && !isMasterVariant) {
            deleteAlternateMutation.mutate(alternateData.id);
        } else if (value.length === 0 && isMasterVariant) {
            setText(initialText[0]);
        } else {
            setText(value);
        }
        if ((value.trim().length > 1 && isMasterVariant) || !isMasterVariant) {
            updateMutation.mutate(
                {
                    id: alternateData.id,
                    data: { content: value },
                },
                {
                    onError: (error) => {
                        setItemError(error.message);
                    },
                    onSuccess: () => {
                        setItemError(undefined);
                    },
                },
            );

            if (isMasterVariant) {
                debounceContentUpdate({
                    id: currentMessageContentId!,
                    structure: getUpdatedFormulaStructure(
                        value,
                        alternateData.component_id,
                        editorDetails.formula?.structure,
                    ),
                });
            }
        }
    };

    const togglePicker = (e: any) => {
        e.stopPropagation();
        if (anchorEl) {
            setAnchorEl(null);
            setSelectedAlternate(null);
        } else {
            setSelectedAlternate(alternateData.id);
        }
    };

    const onEmojiSelect = (e: any, emoji: any) => {
        // Set flag to prevent blur handler from triggering a redundant save
        isEmojiSelectingRef.current = true;
        if (inputRef && inputRef.current) {
            const cursorPosition = (inputRef.current as any).selectionStart;
            const textBeforeCursor = text.substring(0, cursorPosition);
            const textAfterCursor = text.substring(cursorPosition, text.length);
            cursorPositionRef.current = (textBeforeCursor + emoji.emoji).length;
            const updatedText =
                textBeforeCursor + emoji.emoji + textAfterCursor;
            setText(updatedText);
            setAnchorEl(null);
            updateMutation.mutate(
                {
                    id: alternateData.id,
                    data: { content: updatedText },
                },
                {
                    onError: (error) => {
                        setItemError(error.message);
                    },
                    onSuccess: () => {
                        setItemError(undefined);
                    },
                },
            );
            (inputRef.current as HTMLInputElement).focus();
        }
        setAnchorEl(null);
        // Clear flag after emoji selection is complete
        isEmojiSelectingRef.current = false;
    };

    return (
        <Item
            highlighted={highlighted}
            isMasterVariant={isMasterVariant}
            index={index}
            selected={selected}
            isPasteButtonShow={isPasteButtonShow}
        >
            <AlternateListItemContent
                alternateDeleteMutation={deleteAlternateMutation}
                alternateText={text}
                setAlternateText={setText}
                setSelectedAlternate={setSelectedAlternate}
                setAnchorEl={setAnchorEl}
                alternateData={alternateData}
                selected={selected}
                anchorEl={anchorEl}
                saveChanges={saveChanges}
                inputRef={inputRef}
                togglePicker={togglePicker}
                isEditable={isEditable}
                onEmojiSelect={onEmojiSelect}
                error={itemError}
                isMasterVariant={isMasterVariant}
                isEmojiSelectingRef={isEmojiSelectingRef}
            />
        </Item>
    );
};
const Item = styled.div.withConfig({
    shouldForwardProp: (prop) =>
        ![
            'isPasteButtonShow',
            'isMasterVariant',
            'highlighted',
            'index',
            'selected',
        ].includes(prop),
})<StyledProps>(
    ({ isPasteButtonShow, isMasterVariant, highlighted, index, selected }) => ({
        backgroundColor: isPasteButtonShow
            ? aampeTheme.colors.backgroundWG01
            : isMasterVariant
            ? aampeTheme.colors.gray25
            : highlighted
            ? aampeTheme.colors.primary300
            : aampeTheme.colors.white,
        borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
        borderTop:
            index === 0 ? `1px solid ${aampeTheme.colors.bordersWG01}` : '',
        display: selected ? 'flex' : '',
        '&:hover': {
            backgroundColor: isPasteButtonShow
                ? aampeTheme.colors.backgroundWG01
                : aampeTheme.colors.interactiveWG01,
        },
    }),
);
export default AlternateListItem;
